// ==========================================================================
// StarPlayer ActiveX
// ==========================================================================

import { createElement, toggleClass, closest } from '../utils/elements';
import is from '../utils/is';
import browser from '../utils/browser';
import { setAspectRatio } from '../utils/style';
import support from '../support';
import i18n from '../utils/i18n';
import { replaceAll } from '../utils/strings';
import { dedupe } from '../utils/arrays';
import activex_captions from './activex-captions';
import { parseUrl } from '../utils/urls';
import { fetch as fetchPolyfill } from 'whatwg-fetch';

export const OpenState = {
  CLOSED: 0,
  OPENING: 1,
  OPENED: 2,
  CLOSING: 3,
};
Object.freeze(OpenState);

export const PlayState = {
  STOPPED: 0,
  PLAYING: 1,
  PAUSED: 2,
  BUFFERING_STARTED: 3,
  BUFFERING_STOPPED: 4,
  COMPLETE: 5,
};
Object.freeze(PlayState);

export const CustomEvent = {
  KeyDown: 100,
  ChangedQuality: 101,
  ChangedCaption: 102,
};
Object.freeze(CustomEvent);

function getActivexUrl() {
  if (browser.x64) {
    return STARPLAYER64_URL;
  }
  return STARPLAYER_URL;
}

function getActivexSetupUrl() {
  if (browser.x64) {
    return STARPLAYER64_SETUP_URL;
  }
  return STARPLAYER_SETUP_URL;
}

function getActivexMinimumVersion() {
  if (browser.x64) {
    return STARPLAYER64_VERSION;
  }
  return STARPLAYER_VERSION;
}

function attachIE11Event(obj, _strEventId, _functionCallback) {
  const paramsFromToStringRegex = /\(\)|\([a-zA-Z1-9,\_\-\s]+\)/;
  const params = _functionCallback.toString().match(paramsFromToStringRegex)[0];
  let handler;
  try {
    handler = document.createElement('script');
    handler.setAttribute('for', obj.id);
    handler.event = _strEventId + params;
    handler.appendChild(document.createTextNode('player.' + _strEventId + params + ';'));
    if (document.body) {
      document.body.appendChild(handler);
    }
  } catch (ex) {}
}

export const activex = {
  player_id: 'starplayer',
  controller_id: 'starplayer_controller',
  api: null,
  
  getControllerUrl() {
    const player = this;
    return browser.x64 != true ? player.config.starplayer.controllerUrl : player.config.starplayer.controller64Url;
  },

  getControllerParams() {
    const player = this;
    const config = this.config.quality;
    const options = activex.getQualityOptions.call(this);

    //quality
    // Set options if passed and filter based on uniqueness and config
    if (is.array(options)) {
      this.options.quality = dedupe(options).filter((quality) => this.config.quality.options.includes(quality));
    }

    let quality = [
      this.storage.get('quality'),
      config.selected,
      config.default,
    ].find(is.number);

    if (!options.includes(quality)) {
      const value = closest(options, quality);
      player.debug.warn(`Unsupported quality option: ${quality}, using ${value} instead`);
      quality = value;
    }

    const qualitylist = this.options.quality.reverse().join(',');
    player.debug.log(qualitylist);


    const tracks = activex_captions.getTracks.call(this);
    // const toggle = Boolean(tracks.length);

    // Generate options data
    let captions = tracks.map((track, value) => activex_captions.getLabel.call(player, track));
    captions.unshift(i18n.get('disabled', this.config));
    const captionlist = captions.join(',');
    // player.debug.log(captionlist);
    const currentTrack = activex_captions.getCurrentTrack.call(player);
    const currentLabel = activex_captions.getLabel.call(this, currentTrack);
    let enabledSpeed = true;
    if (player.config.speed.options.length <= 1) {
      enabledSpeed = false;
    }
    return `<step>${player.config.seekTime}</step><quality>${quality}</quality><qualitylist>${qualitylist}</qualitylist><caption>${currentLabel}</caption><captionlist>${captionlist}</captionlist><limit_range>${this.config.limitedForward}</limit_range><enabled_speed>${enabledSpeed}</enabled_speed>`;
  },

  showMessage(message, passive = true) {
    if (passive && this.message) {
      return Promise.reject(new Error('message already display'));
    }

    this.elements.message.removeAttribute('hidden');
    this.elements.text.innerHTML = message;
    toggleClass(this.elements.container, this.config.classNames.messageEnabled, true);
  },

  getSources() {
    if (!this.isStarPlayer) {
      return [];
    }

    const sources = Array.from(this.media.querySelectorAll('sourcex'));

    // Filter out unsupported sources (if type is specified)
    return sources.filter((source) => {
      const type = source.getAttribute('type');
      if (is.empty(type)) {
        return true;
      }
      return support.mime.call(this, type);
    });
  },

  // getSourceBestOne() {
  //   const sources = Array.from(activex.getSources.call(this)).sort((a, b) => {
  //     return Number(b.getAttribute('size')) - Number(a.getAttribute('size'));
  //   });
  //   return sources[0];
  // },

   // Get quality levels
   getQualityOptions() {
    // Whether we're forcing all options (e.g. for streaming)
    if (this.config.quality.forced) {
      return this.config.quality.options;
    }

    // Get sizes from <source> elements
    return activex.getSources
      .call(this)
      .map((source) => Number(source.getAttribute('size')))
      .filter(Boolean);
  },

  installActivex() {
    const player = this;

    let msg = i18n.get('installActivex', player.config.starplayer);
    const url = getActivexSetupUrl();
    msg = replaceAll(msg, '{activexSetupUrl}', url);
    activex.showMessage.call(player, msg);
  },

  setup() {
    if (!browser.isIE) {
      return;
    }

    const player = this;

    // Store reference
    this.media.plyr = this;

    this.media.hidden = true;

    this.elements.starplayerContainer = createElement('div', { class: 'starplayer__container' });

    this.elements.starplayerVideoContainer = createElement('div', { class: 'starplayer__videoContainer' });

    this.elements.starplayerControllerContainer = createElement('div', { class: 'starplayer__controllerContainer' });

    this.elements.starplayerContainer.appendChild(this.elements.starplayerVideoContainer);

    this.elements.starplayerContainer.appendChild(this.elements.starplayerControllerContainer);

    this.elements.wrapper.appendChild(this.elements.starplayerContainer);

    // Set aspect ratio if fixed
    if (!is.empty(this.config.ratio)) {
      setAspectRatio.call(player);
    }

    activex_captions.setup.call(player);

    // Quality
    Object.defineProperty(player.media, 'quality', {
      get() {
        // Get sources
        const sources = activex.getSources.call(player);
        const source = sources.find((s) => s.getAttribute('src') === player.source);

        // Return size, if match is found
        return source && Number(source.getAttribute('size'));
      },
      set(input) {
        // If we're using an an external handler...
        if (player.config.quality.forced && is.function(player.config.quality.onChange)) {
          player.config.quality.onChange(input);
        } else {
          // Get sources
          const sources = activex.getSources.call(player);
          // Get first match for requested size
          const source = sources.find((s) => Number(s.getAttribute('size')) === input);

          // No matching source found
          if (!source) {
            return;
          }

          // const speed = activex.api.getRate();
          let oldPlayState = null;
          let currentTime = 0;
          const oldOpenState = activex.api.getOpenState();
          if (oldOpenState == OpenState.OPENED) {
            oldPlayState = activex.api.getPlayState();
            currentTime = activex.api.getCurrentPosition();
          }
          
          activex.bindEvent.call(player, "open_state_change", (state) => {
            activex.api.setOpenState(state);
            if (state == OpenState.OPENING) {
              
            }
            if (state == OpenState.OPENED) {
              if (oldOpenState == OpenState.OPENED) {
                activex.api.setCurrentPosition(currentTime);
              }
              if (oldPlayState == PlayState.PLAYING) {
                activex.api.play();
              } else {
                activex.api.pause();
              }
            }
          });

          activex.bindEvent.call(player, "play_state_change", (state) => {
            activex.api.setPlayState(state);
          });

          const contentId = source.getAttribute('contentid') || '';
          const src = source.getAttribute('src');

          if (player.config.starplayer.token.enabled) {
            
            const option = {
                url: player.config.starplayer.token.url,
                apiKey: player.config.starplayer.token.apiKey,
                vod: parseUrl(src).pathname,
            };
            
            fetchPolyfill(option.url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'api_key': option.apiKey,
              },
              body: JSON.stringify({vod: parseUrl(src).pathname,})
            })
            .then((response) => {
              return response.json();
            }).then((json)=> {
              if (!is.empty(json.api_data)) {
                if (is.url(player.config.starplayer.logUrl)) {
                  activex.api.setLearningRecord(player.config.starplayer.logUrl, contentId);
                }
                activex.api.openMedia(src, null, json.api_data);
                activex.initThumbnail.call(player);
              } else {
                starplayer.showMessage.call(player, i18n.get('tokenError', player.config.starplayer));  
              }
            }).catch((e) => {
              starplayer.showMessage.call(player, i18n.get('tokenError', player.config.starplayer));
            });

          } else {
            if (is.url(player.config.starplayer.logUrl)) {
              activex.api.setLearningRecord(player.config.starplayer.logUrl, contentId);
            }
            activex.api.openMedia(src, null, null);
            activex.initThumbnail.call(player);
          }

        }
      },
    });

    setTimeout(() => activex.createController.call(this), 0);
  },

  createController() {
    const player = this;
    let parent_width = this.elements.starplayerControllerContainer.style.width;
    parent_width = parent_width != '' ? parent_width : '100%';
    let parent_height = this.elements.starplayerControllerContainer.style.height;
    parent_height = parent_height != '' ? parent_height : '100%';

    const html = `<object style='position:absolute;left:-9999999px;' id='${
      activex.controller_id
    }' classid='CLSID:7A63FEE6-E174-4FBC-A064-875DB95594A6' width='${parent_width}' height='${parent_height}' codebase='${getActivexUrl()}#version=${getActivexMinimumVersion()}' ></object>`;
    this.elements.starplayerControllerContainer.innerHTML = html;

    const controller = document.querySelector('#' + activex.controller_id);
    controller.onreadystatechange = function () {
      if (this.object) {
        controller.style.position = 'relative';
        controller.style.left = '0px';

        if (controller.HWND) {
          activex.createPlayer.call(player, controller.HWND);
        } else {
          var id = setInterval(function () {
            if (controller.HWND) {
              clearInterval(id);
              activex.createPlayer.call(player, controller.HWND);
            }
          }, 1);
        }
      } else {
        activex.installActivex.call(player);
      }
    };
    if (controller.object) controller.onreadystatechange();
    else if (browser.isAtLeastIE11)
      controller.addEventListener('readystatechange', controller.onreadystatechange, false);
  },

  createPlayer(hwnd) {
    const player = this;

    let parent_width = this.elements.starplayerVideoContainer.style.width;
    parent_width = parent_width != '' ? parent_width : '100%';
    let parent_height = this.elements.starplayerVideoContainer.style.height;
    parent_height = parent_height != '' ? parent_height : '100%';

    const html =
      "<object style='position:absolute;left:-9999999px;' id='" +
      activex.player_id +
      "' classid='CLSID:99277D5A-52B3-4B2E-AC38-B0065575FC55' width='" +
      parent_width +
      "' height='" +
      parent_height +
      "' codebase='" +
      getActivexUrl() +
      '#version=' +
      getActivexMinimumVersion() +
      "' >" +
      "<param name='config' value='" +
      STARPLAYER_CONFIG_URL +
      "' />" +
      "<param name='controller' value='" +
      activex.getControllerUrl.call(this) +
      "' />" +
      "<param name='user_id' value='" +
      player.config.starplayer.userId +
      "' />" +
      "<param name='spk_id' value='" +
      player.config.starplayer.spkId +
      "' />" +
      // "<param name='time_limit' value='" + media.previewTime + "' />" +
      "<param name='auto_play' value='" +
      true +
      "' />" +
      "<param name='video_armode' value='1' />" +
      // "<param name='cpcode' value='" + config.cpcode + "' />" +
      "<param name='controller_container_hwnd' value='" +
      hwnd +
      "' />" +
      "<param name='controller_params' value='" + activex.getControllerParams.call(this) + "' />" +
      // "<param name='enable_block_messenger' value='" + config.blockMessenger + "' />" +
      // "<param name='enable_block_virtual_machine' value='" + config.blockVirtualMachine + "' />" +
      // "<param name='enable_dual_monitor' value='" + config.dualMonitor + "' />" +
      // "<param name='closed_caption_size' value='" + config.captionSize + "' />" +
      "<param name='watermark_text' value='" +
      player.config.starplayer.watermark.text +
      "' />" +
      "<param name='watermark_text_color' value='" +
      '#40ffffff' +
      "' />" +
      // "<param name='watermark_text_size' value='" + config.watermarkTextSize + "' />" +
      "<param name='watermark_horz_align' value='" +
      3 +
      "' />" +
      "<param name='watermark_vert_align' value='" +
      3 +
      "' />" +
      "<param name='watermark_interval' value='" +
      player.config.starplayer.watermark.positionChangeInterval +
      "' />" +
      "<param name='watermark_show_interval' value='" +
      player.config.starplayer.watermark.displayDuration +
      "' />" +
      // "<param name='auto_progressive_download' value='" + config.auto_progressive_download + "' />" +
      // "<param name='marker' value='" + media.marker + "' />" +
      "<param name='controller_mode' value='" +
      1 +
      "' />" +
      // "<param name='custom_log_url' value='" + player.config.starplayer.logUrl + "' />" +
      // "<param name='custom_log_extra' value='" + media.logExtraData + "' />" +
      // "<param name='logo_url' value='" + config.logoUrl + "' />" +
      // "<param name='logo_horz_align' value='" + config.logoHorzAlign + "' />" +
      // "<param name='logo_vert_align' value='" + config.logoVertAlign + "' />" +
      // "<param name='subtitle_placement' value='" + config.subtitlePlacement + "' />" +
      // "<param name='hash' value='" + config.hash + "' />" +
      '</object>';
    this.elements.starplayerVideoContainer.innerHTML = html;

    const starplayerObject = document.querySelector('#' + activex.player_id);
    starplayerObject.style.position = 'relative';
    starplayerObject.style.left = '0px';

    if (browser.isAtLeastIE11) {
      if (starplayerObject.object) {
        activex.api = new StarPlayerApi(starplayerObject);
        starplayerObject.init();
        activex.bindEvents.call(player);
        setTimeout(() => activex.loadUrl.call(player), 10);
      } else {
        activex.installActivex.call(player);
      }
    } else {
      starplayerObject.onreadystatechange = function () {
        if (this.object) {
          activex.api = new StarPlayerApi(starplayerObject);
          starplayerObject.init();
          activex.bindEvents.call(player);

          setTimeout(() => activex.loadUrl.call(player), 10);
        } else {
          activex.installActivex.call(player);
        }
      };

      if (starplayerObject.object) {
        starplayerObject.onreadystatechange();
      }
    }
  },

  loadUrl() {
    const player = this;
    player.quality = null;
  },

  initThumbnail() {
    const player = this;
    if (player.config.previewThumbnails.enabled) {
      const { src } = player.config.previewThumbnails;
      if (is.empty(src)) {
        throw new Error('Missing previewThumbnails.src config attribute');
      }
      const urls = is.string(src) ? [src] : src;

      //player.debug.log(urls[urls.length - 1]);
      activex.api.setThumbnailRootUrl(urls[urls.length - 1]);
    }
  },

  bindEvent(name, value) {
		var id = "_on_" + name;
		if (!this[id])
			this[id] = [];
		this[id].push(value);
	},

  destroy() {
    if (this.elements.starplayerContainer) {
      activex.api.closeMedia();
      this.elements.starplayerContainer.remove();
    }
    this.media.hidden = false;
  },

  bindEvents() {
    const player = this;
    const api = activex.api;
    api.addEvent('open_state_change', (state) => {
      player.open_state_change(state);
    });

    api.addEvent('play_state_change', (state) => {
      player.play_state_change(state);
    });

    api.addEvent('position_change', (pos) => {
      player.position_change(pos);
    });

    api.addEvent('position_change2', (new_pos, old_pos) => {
      player.position_change2(new_pos, old_pos);
    });

    api.addEvent('volume_change', (volume, mute) => {
      player.volume_change(volume, mute);
    });

    api.addEvent('rate_change', (rate) => {
      player.rate_change(rate);
    });

    api.addEvent('repeat_change', (repeat) => {
      player.repeat_change(repeat);
    });

    api.addEvent('repeat_range_change', (start, end) => {
      player.repeat_range_change(start, end);
    });

    api.addEvent('update_time', (time) => {
      player.update_time(time);
    });

    api.addEvent('key_down', (keycode) => {
      player.key_down(keycode);
    });

    api.addEvent('key_up', (keycode) => {
      player.key_up(keycode);
    });

    api.addEvent('mouse_down', (x, y) => {
      player.mouse_down(x, y);
    });

    api.addEvent('mouse_up', (x, y) => {
      player.mouse_up(x, y);
    });

    api.addEvent('mouse_dbclick', (x, y) => {
      player.mouse_dbclick(x, y);
    });

    api.addEvent('mouse_wheel', (x, y, wheelDelta) => {
      player.mouse_wheel(x, y, wheelDelta);
    });

    api.addEvent('error', (errCode) => {
      player.error(errCode);
    });

    api.addEvent('close', () => {
      player.close();
    });

    api.addEvent('custom', (type, value) => {
			player.custom(type, value);
		});
  },
};

class StarPlayerApi {
  constructor(obj) {
    this.player = obj;
    this.visible = false;
    this.openState = -1;
    this.playState = -1;
  }

  url() {
    return this.player.url;
  }

  openMedia(url, cc, token) {
    if (token && token.length > 0 && typeof token != 'undefined') {
      this.player.open_media2(url, cc, token);
    } else { 
      this.player.open_media(url, cc);
    }
  }

  closeMedia() {
    this.player.close();
  }

  getDuration() {
    return this.player.duration;
  }

  getCurrentPosition() {
    return this.player.current_position;
  }

  setCurrentPosition(pos) {
    this.player.current_position = pos;
  }

  getVolume() {
    return this.player.volume;
  }

  setVolume(volume) {
    this.player.volume = volume;
  }

  getMute() {
    return this.player.mute;
  }

  setMute(mute) {
    this.player.mute = mute;
  }

  getFullscreen() {
    return this.player.fullscreen;
  }

  setFullscreen(fullscreen) {
    this.player.fullscreen = fullscreen;
  }

  getRate() {
    return this.player.rate;
  }

  setRate(rate) {
    this.player.rate = rate;
  }

  getRepeat() {
    return this.player.repeat;
  }

  setRepeat(repeat) {
    this.player.repeat = repeat;
  }

  getRepeatStartTime() {
    return this.player.repeat_start_time;
  }

  setRepeatStartTime(time) {
    this.player.repeat_start_time = time;
  }

  getRepeatEndTime() {
    return this.player.repeat_end_time;
  }

  setRepeatEndTime(time) {
    this.player.repeat_end_time = time;
  }

  getPlayTime() {
    return this.player.play_time;
  }

  initPlayTime() {
    this.player.init_play_time();
  }

  play() {
    this.player.play();
  }

  pause() {
    this.player.pause();
  }

  stop() {
    this.player.stop();
  }

  addEvent(name, func) {
    if (this.player.attachEvent) {
      this.player.attachEvent(name, func);
    } else {
      attachIE11Event(this.player, name, func);
    }
  }

  setOpenState(state) {
    this.openState = state;
  }

  getOpenState() {
    return this.openState;
  }

  setPlayState(state) {
    this.playState = state;
  }

  getPlayState() {
    return this.playState;
  }

  getVisible() {
    return this.visible;
  }

  setVisible(value) {
    if (is.isIE) {
      if (value) {
        this.player.style.position = 'relative';
        this.player.style.left = '0px';
      } else {
        this.player.style.position = 'absolute';
        this.player.style.left = '-9999999px';
      }
    } else {
      this.player.style.visibility = value ? 'visible' : 'hidden';
    }
    this.visible = value;
  }

  getVideoWidth() {
    return this.player.video_width;
  }

  getVideoHeight() {
    return this.player.video_height;
  }

  getPid() {
    return this.player.pid;
  }

  setTimelimit() {
    this.player.timelimit = time;
  }

  getTrack() {
    return this.player.track;
  }

  setTrack(value) {
    this.player.track = value;
  }

  getProgressRate() {
    return this.player.progress_rate;
  }

  getStopPosition() {
    return this.player.stop_position;
  }

  setThumbnailRootUrl(url) {
		this.player.thumbnail_root_url = url;
	}

  setCaption(text) {
    this.player.set_caption(text);
  }

  clearCaption() {
    this.player.set_caption('');
  }

  setLogUrl(logUrl) {
    this.player.custom_log_url = logUrl;
  }

  setLogExtraData(contentId) {
    this.player.custom_log_extra = contentId;
  }

  setLearningRecord(logUrl, contentId) {
    this.player.custom_log_url = logUrl;
    this.player.custom_log_extra = contentId;
  }
}

// export default { activex, OpenState, PlayState };
export default activex;
